import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Loader2, Mail, ArrowLeft } from 'lucide-react';
import { BrandLogo } from '@/components/BrandLogo';

export default function StudentForgotPage() {
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post('/student/forgot', { email: email.trim() });
            setDone(true);
            toast.success('Request received.');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-brand-orb px-4 py-10 sm:px-6">
            <div className="pointer-events-none absolute -top-32 left-1/2 h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-blob-lavender" />
            <div className="pointer-events-none absolute -bottom-24 right-0 h-96 w-96 rounded-full bg-blob-periwinkle blur-3xl" />

            <div className="relative z-10 w-full max-w-md">
                <div className="mb-8 text-center">
                    <BrandLogo size="lg" to="/" className="mx-auto" />
                    <p className="mt-3 text-sm text-muted-foreground">Reset your password</p>
                </div>
                <Card className="border-border/60 shadow-[0_20px_60px_rgba(12,12,44,0.08)]">
                    <CardContent className="p-8">
                        {done ? (
                            <div className="text-center">
                                <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-accent/20 text-accent">
                                    <Mail className="h-6 w-6" />
                                </div>
                                <h1 className="font-display text-xl font-semibold text-primary">Request received</h1>
                                <p className="mt-2 text-sm text-muted-foreground">
                                    If your email is on the roster, a fresh magic link has been generated.
                                    Please ask your college admin to share it with you.
                                </p>
                                <Link to="/">
                                    <Button variant="secondary" className="mt-6 w-full">Back to sign in</Button>
                                </Link>
                            </div>
                        ) : (
                            <>
                                <h1 className="font-display text-2xl font-semibold text-primary">Forgot your password?</h1>
                                <p className="mt-1 text-sm text-muted-foreground">Enter your college email and we’ll generate a fresh magic link for you.</p>
                                <form onSubmit={submit} className="mt-6 space-y-4">
                                    <div>
                                        <Label htmlFor="email">College email</Label>
                                        <Input
                                            id="email"
                                            type="email"
                                            required
                                            placeholder="you@college.edu"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            data-testid="forgot-email"
                                        />
                                    </div>
                                    <Button type="submit" size="lg" className="w-full" disabled={loading} data-testid="forgot-submit">
                                        {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />}
                                        Request magic link
                                    </Button>
                                </form>
                            </>
                        )}
                        <Link to="/" className="mt-6 flex items-center justify-center gap-1 text-xs text-muted-foreground hover:text-foreground">
                            <ArrowLeft className="h-3 w-3" /> Back to sign in
                        </Link>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
